import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { authService } from '../../services/authService';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const currentYear = new Date()?.getFullYear();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const { error: resetError } = await authService?.resetPassword(email);

      if (resetError) {
        setError(resetError?.message || 'Failed to send reset email. Please try again.');
        setIsLoading(false);
        return;
      }

      setIsSuccess(true);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password - HungryElph | Reset Your Password</title>
        <meta
          name="description"
          content="Reset your HungryElph account password. Enter your email to receive password reset instructions."
        />
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl shadow-warm-lg p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Lock" size={32} color="var(--color-primary)" />
              </div>
            </div>

            {!isSuccess ? (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    No worries! Enter your email and we'll send you reset instructions.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e?.target?.value);
                      if (error) setError('');
                    }}
                    error={error}
                    required
                  />

                  {error && (
                    <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                      <p className="text-sm text-destructive flex-1">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    loading={isLoading}
                    iconName="Send"
                    iconPosition="right"
                  >
                    Send Reset Link
                  </Button>

                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-smooth"
                  >
                    <Icon name="ArrowLeft" size={16} />
                    Back to Sign In
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={32} color="var(--color-success)" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                    Check Your Email
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mb-4">
                    We've sent password reset instructions to:
                  </p>
                  <p className="text-base font-medium text-foreground mb-6">
                    {email}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Didn't receive the email? Check your spam folder or try again.
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="default"
                    fullWidth
                    onClick={() => navigate('/login')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back to Sign In
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => {
                      setIsSuccess(false);
                      setEmail('');
                    }}
                  >
                    Try Different Email
                  </Button>
                </div>
              </>
            )}
          </div>

          <footer className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} HungryElph. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;